import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../utils/api";
import { formatLapTime } from "../utils/formatters";
import { parseWeather } from "../utils/weatherParser";
import { WeatherTooltip } from "./WeatherTooltip";

export function relativeDate(dateStr: string): string {
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 14) return "Last week";
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  return d.toLocaleDateString();
}

export function eventBadgeColor(eventType: string): string {
  switch (eventType?.toLowerCase()) {
    case "race": return "var(--good)";
    case "qualifying": return "var(--warn)";
    case "practice": return "var(--text2)";
    default: return "var(--text2)";
  }
}

export function SessionCard({ session, showDriver = false }: { session: any, showDriver?: boolean }) {
  const [manifest, setManifest] = useState<any>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    let timer: any;
    if (isHovered) {
      timer = setTimeout(async () => {
        if (!manifest) {
          try {
            const data = await api<any>(`/api/sessions/${session.id}/lap-manifest`);
            setManifest(data);
          } catch (err) {
            console.error("Preview fetch failed", err);
          }
        }
        setShowPreview(true);
      }, 250);
    } else {
      setShowPreview(false);
    }
    return () => clearTimeout(timer);
  }, [isHovered, manifest, session.id]);

  // manifest loaded on hover for preview popup

  return (
    <div style={{ position: "relative" }}>
      <Link
        to={`/session/${session.id}`}
        style={{
          display: "block",
          padding: "16px",
          background: "var(--card)",
          borderRadius: "var(--radius)",
          border: "1px solid var(--border)",
          textDecoration: "none",
          color: "inherit",
          transition: "transform 0.2s, border-color 0.2s",
          height: "100%"
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = "translateY(-2px)";
          e.currentTarget.style.borderColor = "var(--accent)";
          setIsHovered(true);
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.borderColor = "var(--border)";
          setIsHovered(false);
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
          <div style={{ fontSize: "11px", color: "var(--text2)" }}>
            {relativeDate(session.sessionDate)}
          </div>
          {session.eventType && (
            <div style={{
              fontSize: "10px",
              fontWeight: 600,
              textTransform: "uppercase",
              padding: "2px 6px",
              borderRadius: "3px",
              color: eventBadgeColor(session.eventType),
              background: `color-mix(in srgb, ${eventBadgeColor(session.eventType)} 10%, transparent)`,
              border: `1px solid color-mix(in srgb, ${eventBadgeColor(session.eventType)} 25%, transparent)`,
            }}>
              {session.eventType}
            </div>
          )}
        </div>
        
        {showDriver && session.driverName && (
          <div style={{ fontSize: "10px", fontWeight: 700, color: "var(--accent)", textTransform: "uppercase", marginBottom: "4px" }}>
            {session.driverName}
          </div>
        )}

        <h4 style={{ marginBottom: "4px", color: "var(--text)" }}>
          {session.trackName}
        </h4>
        <div style={{ fontSize: "13px", color: "var(--text2)" }}>
          {session.carName}
        </div>

        {/* OT-208: Quick stats badges */}
        {(session.lapCount > 0 || session.bestLapTime || session.stintCount > 0) && (
          <div style={{ display: "flex", gap: "8px", marginTop: "8px", flexWrap: "wrap" }}>
            {session.lapCount > 0 && (
              <span style={{ fontSize: "10px", color: "var(--text3)", fontVariantNumeric: "tabular-nums" }}>
                {session.lapCount} {session.lapCount === 1 ? "lap" : "laps"}
              </span>
            )}
            {session.bestLapTime && session.bestLapTime > 30 && (
              <span style={{ fontSize: "10px", color: "var(--accent)", fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
                {formatLapTime(session.bestLapTime)}
              </span>
            )}
            {session.stintCount > 0 && (
              <span style={{ fontSize: "10px", color: "var(--text3)" }}>
                {session.stintCount} {session.stintCount === 1 ? "run" : "runs"}
              </span>
            )}
          </div>
        )}

        {/* OT-237: Setup name — strip folder path, show only .sto filename */}
        {session.metadata?.SetupName && session.metadata.SetupName !== "Unknown" && (
          <div style={{ marginTop: "6px", fontSize: "10px", color: "var(--text3)" }}>
            Setup: {session.metadata.SetupName.split("/").pop()?.split("\\").pop() || session.metadata.SetupName}
          </div>
        )}
      </Link>

      {showPreview && manifest && (() => {
        // Compute best rolling average (30 → 15 → 10 fallback)
        // Don't require isValid — many laps don't have that flag set
        const validLaps = (manifest.laps || []).filter((l: any) => l.lapTime > 10 && l.lapNum > 0);
        const times = validLaps.map((l: any) => l.lapTime);
        const lapCount = times.length;

        function bestRollingAvg(arr: number[], window: number): number | null {
          if (arr.length < window) return null;
          let best = Infinity;
          for (let i = 0; i <= arr.length - window; i++) {
            const avg = arr.slice(i, i + window).reduce((a, b) => a + b, 0) / window;
            if (avg < best) best = avg;
          }
          return best;
        }

        const avg30 = bestRollingAvg(times, 30);
        const avg15 = bestRollingAvg(times, 15);
        const avg10 = bestRollingAvg(times, 10);
        const bestAvg = avg30 ?? avg15 ?? avg10;
        const avgLabel = avg30 ? "30-Lap Avg" : avg15 ? "15-Lap Avg" : avg10 ? "10-Lap Avg" : `${lapCount}-Lap Avg`;
        const avgLapTime = bestAvg ?? (lapCount > 0 ? times.reduce((s: number, t: number) => s + t, 0) / lapCount : null);

        return (
        <div style={{
          position: "absolute",
          top: "calc(100% + 8px)",
          left: 0,
          right: 0,
          zIndex: 100,
          background: "var(--bg2)",
          border: "1px solid var(--accent)",
          borderRadius: "8px",
          padding: "16px",
          boxShadow: "0 12px 32px rgba(0,0,0,0.6)",
          pointerEvents: "none",
          animation: "fadeIn 0.15s ease-out"
        }}>
          <div style={{ fontSize: "10px", fontWeight: 700, color: "var(--accent)", textTransform: "uppercase", marginBottom: "12px", borderBottom: "1px solid var(--border)", paddingBottom: "4px" }}>
            Session Preview
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px" }}>
            <div>
              <div style={{ fontSize: "10px", color: "var(--text3)", textTransform: "uppercase" }}>{avgLabel}</div>
              <div style={{ fontSize: "15px", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{avgLapTime ? formatLapTime(avgLapTime) : "—"}</div>
            </div>
            <div>
              <div style={{ fontSize: "10px", color: "var(--text3)", textTransform: "uppercase" }}>Laps</div>
              <div style={{ fontSize: "15px", fontWeight: 700 }}>{validLaps.length}</div>
            </div>
            <div>
              <div style={{ fontSize: "10px", color: "var(--text3)", textTransform: "uppercase" }}>Runs</div>
              <div style={{ fontSize: "15px", fontWeight: 700 }}>{manifest.stints?.length || 0}</div>
            </div>
          </div>

          {session.weather && Object.keys(session.weather).length > 0 && (() => {
            const pw = parseWeather(session.weather);
            if (!pw) return null;
            return <WeatherTooltip weather={pw} />;
          })()}

          <div style={{ marginTop: "12px", paddingTop: "8px", borderTop: "1px solid var(--border)", fontSize: "10px", color: "var(--text3)", textAlign: "center" }}>
            {new Date(session.sessionDate).toLocaleString()}
          </div>
        </div>
        );
      })()}
    </div>
  );
}
